import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

interface IndexedDBState<T> {
    data: T[];
    isLoading: boolean;
    error: Error | null;
}

interface IndexedDBConfig {
    dbName: string;
    version: number;
    storeName: string;
    keyPath: string;
}

function createInitialState<T>(): IndexedDBState<T> {
    return {
        data: [],
        isLoading: false,
        error: null,
    };
}

// Create a function to build the slice
export function createIndexedDBSlice<T>(config: IndexedDBConfig) {
    const sliceName = `${config.storeName}Slice`;

    // Create thunks
    const fetchAllItems = createAsyncThunk(`${sliceName}/fetchAllItems`, async (_, { rejectWithValue }) => {
        try {
            const response = await fetch(`/api/${config.storeName}`);
            if (!response.ok) {
                throw new Error("Failed to fetch items");
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error);
        }
    });

    const addItem = createAsyncThunk(`${sliceName}/addItem`, async (item: T, { rejectWithValue }) => {
        try {
            const response = await fetch(`/api/${config.storeName}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(item),
            });
            if (!response.ok) {
                throw new Error("Failed to add item");
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error);
        }
    });

    const updateItem = createAsyncThunk(`${sliceName}/updateItem`, async (item: T, { rejectWithValue }) => {
        try {
            const response = await fetch(`/api/${config.storeName}/${(item as any)[config.keyPath]}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(item),
            });
            if (!response.ok) {
                throw new Error("Failed to update item");
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error);
        }
    });

    const deleteItem = createAsyncThunk(`${sliceName}/deleteItem`, async (key: string | number, { rejectWithValue }) => {
        try {
            const response = await fetch(`/api/${config.storeName}/${key}`, {
                method: "DELETE",
            });
            if (!response.ok) {
                throw new Error("Failed to delete item");
            }
            return key;
        } catch (error) {
            return rejectWithValue(error);
        }
    });

    const slice = createSlice({
        name: sliceName,
        initialState: createInitialState<T>(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                // Fetch all items
                .addCase(fetchAllItems.pending, (state) => {
                    state.isLoading = true;
                    state.error = null;
                })
                .addCase(fetchAllItems.fulfilled, (state, action) => {
                    state.isLoading = false;
                    state.data = action.payload;
                })
                .addCase(fetchAllItems.rejected, (state, action) => {
                    state.isLoading = false;
                    state.error = action.payload as Error;
                })
                // Add item
                .addCase(addItem.pending, (state) => {
                    state.isLoading = true;
                    state.error = null;
                })
                .addCase(addItem.fulfilled, (state, action) => {
                    state.isLoading = false;
                    state.data.push(action.payload);
                })
                .addCase(addItem.rejected, (state, action) => {
                    state.isLoading = false;
                    state.error = action.payload as Error;
                })
                // Update item
                .addCase(updateItem.pending, (state) => {
                    state.isLoading = true;
                    state.error = null;
                })
                .addCase(updateItem.fulfilled, (state, action) => {
                    state.isLoading = false;
                    const index = state.data.findIndex((item) => (item as any)[config.keyPath] === (action.payload as any)[config.keyPath]);
                    if (index !== -1) {
                        state.data[index] = action.payload;
                    }
                })
                .addCase(updateItem.rejected, (state, action) => {
                    state.isLoading = false;
                    state.error = action.payload as Error;
                })
                // Delete item
                .addCase(deleteItem.pending, (state) => {
                    state.isLoading = true;
                    state.error = null;
                })
                .addCase(deleteItem.fulfilled, (state, action) => {
                    state.isLoading = false;
                    state.data = state.data.filter((item) => (item as any)[config.keyPath] !== action.payload);
                })
                .addCase(deleteItem.rejected, (state, action) => {
                    state.isLoading = false;
                    state.error = action.payload as Error;
                });
        },
    });

    return {
        reducer: slice.reducer,
        actions: {
            fetchAllItems,
            addItem,
            updateItem,
            deleteItem,
        },
    };
}

interface IndexedDBItem {
    id: string;
    // Add other fields as needed
}

export const indexedDBSlice = createIndexedDBSlice<IndexedDBItem>({
    dbName: "win11Clone",
    version: 1,
    storeName: "files",
    keyPath: "id",
});

export const { reducer } = indexedDBSlice;
